import React from "react";
import { Link } from "react-router-dom";
import { resolveImage } from "@/lib/api";
import { ArrowUpRight, Gauge, Fuel, Settings2, CalendarDays } from "lucide-react";
import { useI18n } from "@/i18n/LanguageContext";

function formatPrice(n, lang) {
  const locale = lang === "gr" ? "el-GR" : "en-US";
  try {
    return new Intl.NumberFormat(locale, {
      style: "currency",
      currency: "EUR",
      maximumFractionDigits: 0,
    }).format(n);
  } catch {
    return `€${n}`;
  }
}

function statusClass(status) {
  if (status === "reserved") return "status-reserved";
  if (status === "sold") return "status-sold";
  return "status-available";
}

export default function VehicleCard({ vehicle }) {
  const { t, lang } = useI18n();
  const img = vehicle.images?.[0] ? resolveImage(vehicle.images[0]) : "";
  const statusText = t(`vehicle.status.${vehicle.status}`);
  const mileage = (vehicle.mileage || 0).toLocaleString(lang === "gr" ? "el-GR" : "en-US");

  return (
    <Link
      to={`/garage/${vehicle.id}`}
      data-testid={`vehicle-card-${vehicle.id}`}
      className="group block bg-[#121212] border border-white/[0.06] hover:border-[#C6A15B]/40 transition-colors duration-500"
    >
      <div className="relative aspect-[4/3] overflow-hidden bg-[#1A1A1A]">
        {img ? (
          <img
            src={img}
            alt={`${vehicle.brand} ${vehicle.model}`}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-[900ms] ease-out"
            loading="lazy"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-zinc-600 text-xs uppercase tracking-widest">
            No Image
          </div>
        )}
        <div className="absolute inset-x-0 top-0 p-4 flex items-start justify-between">
          <span
            className={`status-chip ${statusClass(vehicle.status)}`}
            data-testid="vehicle-status"
          >
            {statusText}
          </span>
          <span className="w-9 h-9 flex items-center justify-center bg-black/50 border border-white/10 text-white group-hover:bg-[#C6A15B] group-hover:text-black group-hover:border-[#C6A15B] transition-colors">
            <ArrowUpRight size={16} />
          </span>
        </div>
      </div>

      <div className="p-5 flex flex-col gap-4">
        <div className="flex items-start justify-between gap-4">
          <div>
            <div className="text-[11px] tracking-[0.3em] uppercase text-[#C6A15B]">
              {vehicle.brand}
            </div>
            <h3 className="font-display text-xl text-white mt-1 leading-tight">
              {vehicle.model}
            </h3>
          </div>
          <div className="text-right">
            <div className="text-[10px] tracking-[0.3em] uppercase text-zinc-500">
              {t("vehicle.price")}
            </div>
            <div className="font-display text-lg text-white mt-1">
              {formatPrice(vehicle.price, lang)}
            </div>
          </div>
        </div>

        <div className="grid grid-cols-4 gap-3 pt-4 border-t border-white/5">
          <Spec icon={<CalendarDays size={13} />} label={t("vehicle.spec.year")} value={vehicle.year} />
          <Spec icon={<Gauge size={13} />} label={t("vehicle.spec.km")} value={mileage} />
          <Spec icon={<Fuel size={13} />} label={t("vehicle.spec.fuel")} value={vehicle.fuel} />
          <Spec icon={<Settings2 size={13} />} label={t("vehicle.spec.transmission.short")} value={vehicle.transmission} />
        </div>
      </div>
    </Link>
  );
}

function Spec({ icon, label, value }) {
  return (
    <div className="flex flex-col gap-1">
      <div className="flex items-center gap-1.5 text-zinc-500 text-[9px] tracking-[0.25em] uppercase">
        {icon}
        {label}
      </div>
      <div className="text-zinc-200 text-xs font-medium truncate">{value}</div>
    </div>
  );
}
